import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/atoms/button/Button';
import IconButton from 'components/atoms/button/IconButton';
import Form from 'components/atoms/form/Form';
import Icon from 'components/atoms/icon/Icon';
import AutoCompletInput from 'components/atoms/input/autocomplet-input/AutoCompletInput';
import Input from 'components/atoms/input/Input';
import Label from 'components/atoms/input/input-label/InputLabel';
import LoadingScreen from 'components/atoms/loading-screen/LoadingScreen';
import TextArea from 'components/atoms/text-area/TextArea';
import ToggleSwitch from 'components/molecules/toggle-switch/ToggleSwitch';
import { customStyles } from 'lib/helpers/configs/customStyles';
import { IconType } from 'lib/helpers/constants/iconTypes';
import withModal from 'lib/helpers/hoc/withModal'
import { useUserWithoutTeam } from 'lib/hooks/queries/User/useUserWithoutTeam';
import React, { FC, useMemo } from 'react'
import { Controller, FieldArrayPath, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import { useCreateNewTeam } from "lib/hooks/mutations/Settings/useCreateNewTeam";
import { useUpdateTeam } from "lib/hooks/mutations/Settings/useUpdateTeam";
import { useLocation } from "react-router-dom";


interface CreateAndUpdateTeamProps {
    data?: any;
    onConfirm: () => void;
    isUpdate?: boolean;
}

const CreateAndUpdateTeam: FC<CreateAndUpdateTeamProps> = ({ data, onConfirm, isUpdate = false }) => {

    const { t } = useTranslation()

    const { pathname } = useLocation()


    const isBroker = useMemo(() => {

        if (!data) {
            return pathname.includes('brokers');
        }
        return data.type === 'broker';

    }, [pathname, data]);


    const schema = yup.object().shape({
        name: yup.string().required("This field is required"),
        description: yup.string().nullable(),
        is_active: yup.boolean(),
        type: yup.string().required("This field is required"),
        team_leader_id: yup.string().required("This field is required"),
        team_admin_id: yup
            .string()
            .when("type", {
                is: (val: string) => val === "broker",
                then: (schema) => schema.required("This field is required for brokers"),
                otherwise: (schema) => schema.notRequired(),
            }),
        team_members_ids: yup
            .array()
            .min(1, "At least one team member is required"),
    });

    const {
        formState: { errors },
        handleSubmit,
        register,
        control,
        reset,
        watch,
    } = useForm({
        defaultValues: isUpdate ? {
            name: data.name,
            description: data.name,
            type: data.type,
            is_active: data.is_active,
            team_leader_id: data.leader.id,
            team_admin_id: data.admin?.id || '',
            team_members_ids: data.members.map((item: any) => {
                return {
                    value: item.id,
                    label: item.name + " " + item.last_name,
                }
            }),
        } : {
            name: "",
            description: "",
            is_active: true,
            type: isBroker ? 'broker' : 'internal',
            team_leader_id: "",
            team_admin_id: "",
            team_members_ids: [],
        },
        resolver: yupResolver(schema),
    });

    const {
        fields: workers,
        append: appendWorker,
        remove: removeWorker,

    } = useFieldArray<any>({
        control,
        name: "team_members_ids" as FieldArrayPath<any>,
    });

    const { data: users, isLoading } = useUserWithoutTeam()

    const formmatedData = useMemo(() => {
        if (!users) return

        let formmatedUsers = (users as any)?.data.map((user: any) => {
            return {
                value: user.id,
                label: user.name + " " + user.last_name,
            }
        })
        if (data) {
            formmatedUsers.push({
                value: data.leader.id,
                label: data.leader.name + " " + data.leader.last_name,
            })
            if (data.admin) {
                formmatedUsers.push({
                    value: data.admin.id,
                    label: data.admin.name + " " + data.admin.last_name,
                })
            }
        }

        return formmatedUsers
    }, [users, data])

    const watchAdminId = watch("team_admin_id");
    const watchLeaderId = watch("team_leader_id");
    const watchMembers = watch("team_members_ids");
    const watchType = watch("type");

    const filteredOptions = useMemo(() => {
        let watchMembersIds = watchMembers || [];
        let checkedAdminId = watchType === 'broker' ? watchAdminId : null;

        const selectedIds = new Set([
            checkedAdminId,
            watchLeaderId,
            ...watchMembersIds.map((m: any) => m.value),
        ]);

        return formmatedData?.filter(
            (option: any) => !selectedIds.has(option.value)
        );

    }, [formmatedData, watchAdminId, watchLeaderId, watchMembers, watchType]);


    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key === "Enter") {

            const worker: any = filteredOptions?.find(
                (worker: any) =>
                    worker.label === (event.target as HTMLInputElement).value
            );

            if (worker) {
                appendWorker(worker);

            }

        }
    };

    const handleRemove = (index: number) => {
        removeWorker(index);
    };

    const { mutateAsync: createNewTeam, isLoading: isLoadingCreate } = useCreateNewTeam()

    const { mutateAsync: updateTeam, isLoading: isLoadingUpdate } = useUpdateTeam(isBroker)

    const onSubmit = (formData: Record<string, any>) => {

        let finalData: any = {
            ...formData,
            team_members_ids: formData.team_members_ids.map((item: any) => item.value),
        };

        if (formData.type !== 'broker') {
            delete finalData.team_admin_id;
        }


        if (isUpdate) {
            finalData = {
                ...finalData,
                team_id: data.id,
            }

            updateTeam(finalData).then(() => {
                onConfirm()
            })
        } else {
            createNewTeam(finalData).then(() => {
                onConfirm()
            })
        }


    }



    if (isLoading) {
        return (
            <div className='py-20 relative'>
                <LoadingScreen />
            </div>
        )
    }

    return (
        <Form onSubmit={handleSubmit(onSubmit)} className="mb-0 flex flex-col gap-5">

            <Input
                label={t("Team Name")}
                placeholder={t("Enter team name")}
                {...register("name", { required: t("This field is required") })}
                error={errors?.name}
            />
            {watch("type") === "broker" && (
                <div className="relative">
                    <Label>{t('Team Admin')}</Label>
                    <div>
                        <Controller
                            name="team_admin_id"
                            control={control}
                            rules={{ required: t("This field is required") }}
                            render={({ field }) => {

                                let selectedUser = formmatedData.find((option: any) => option.value === field.value);

                                return (
                                    <CreatableSelect<any, false>
                                        {...field}
                                        placeholder={t("Choose Team Admin")}
                                        options={filteredOptions}
                                        value={selectedUser}
                                        isValidNewOption={() => false}
                                        className="cursor-pointer font-inter-regular react-select-container"
                                        classNamePrefix="react-select"
                                        onChange={(newValue: any, actionMeta: any) => {

                                            if (
                                                actionMeta.action === "select-option" &&
                                                newValue?.value
                                            ) {
                                                field.onChange(newValue.value);
                                            }
                                        }}

                                        styles={customStyles}
                                        menuPosition={"fixed"}
                                        menuPortalTarget={document.body}
                                    />
                                )
                            }}
                        />
                        {errors.team_admin_id?.message && (
                            <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                                <>{errors.team_admin_id?.message}*</>
                            </div>
                        )}

                    </div>

                </div>
            )}

            <div className="relative">
                <Label>{t('Team Leader')}</Label>
                <div>
                    <Controller
                        name="team_leader_id"
                        control={control}
                        rules={{ required: t("This field is required") }}
                        render={({ field }) => {

                            let selectedUser = formmatedData.find((option: any) => option.value === field.value);

                            return (
                                <CreatableSelect<any, false>
                                    {...field}
                                    placeholder={t("Choose Team Leader")}
                                    options={filteredOptions}
                                    value={selectedUser || null}
                                    isValidNewOption={() => false}
                                    className="cursor-pointer font-inter-regular react-select-container"
                                    classNamePrefix="react-select"
                                    onChange={(newValue: any, actionMeta: any) => {
                                        if (
                                            actionMeta.action === "select-option" &&
                                            newValue?.value
                                        ) {
                                            field.onChange(newValue.value);
                                        }
                                    }}
                                    styles={customStyles}
                                    menuPosition={"fixed"}
                                    menuPortalTarget={document.body}
                                />
                            )
                        }}
                    />
                    {errors.team_leader_id?.message && (
                        <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                            <>{errors.team_leader_id?.message}*</>
                        </div>
                    )}

                </div>

            </div>
            <div>
                <Label className="">
                    {t('Team Members')}
                </Label>

                <AutoCompletInput
                    suggestions={filteredOptions?.map((item: any) => item.label)}
                    onKeyDown={handleKeyDown}
                    className="block"
                    placeholder={t('Enter member name')}
                    name="team_members_ids"
                    error={errors?.team_members_ids}

                />
                <ul className="flex flex-wrap gap-2">
                    {workers?.map((item, index) => (
                        <li
                            key={`partner-key-${index}`}
                            className="font-inter-bold mt-2 pr-[25px] capitalize bg-[#F4F9FD] text-[#7D8592] relative text-center rounded-[8px] text-[14px] w-auto p-1"
                        >
                            {(item as any).label}
                            <Button
                                type="button"
                                className="absolute top-[7px] right-[5px]"
                                onClick={() => handleRemove(index)}
                            >
                                <Icon
                                    iconType={IconType.CLOSE_ICON}
                                    className="p-1"
                                    color="#7D8592"
                                />
                            </Button>
                        </li>
                    ))}
                </ul>
            </div>
            <TextArea
                placeholder={t("Write description ..")}
                label={t("Description")}
                {...register("description")}
                error={errors?.description}
            />
            <ToggleSwitch
                label="Active"
                classes={{ label: "!flex flex-row-reverse gap-[15px]" }}
                {...register("is_active")}
                error={errors?.is_active}
            />
            <div className="flex gap-5">
                <IconButton
                    secondary={true}
                    type="button"
                    onClick={() => { reset() }}
                    className='w-full'
                    disabled={isLoadingCreate || isLoadingUpdate}

                >
                    {t("Reset")}
                </IconButton>
                <IconButton
                    disabled={isLoadingCreate || isLoadingUpdate}
                    icon={isUpdate ? null : <Icon iconType={IconType.PLUS_ICON} />}
                    className='w-full'
                >
                    {isUpdate ? t("Update") : t("Create")}
                </IconButton>
            </div>
        </Form>
    )
}

export default withModal(CreateAndUpdateTeam)